import { OvFormInput } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material'
import { ISendEmailRequest, ISingleDocument, EmailTemplateType } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'

interface SendEmailFormData extends ISendEmailRequest {
  recipient_email: string
  recipient_name?: string
  subject?: string
  body?: string
  attach_pdf?: boolean
}

interface SendEmailFormProps {
  open: boolean
  onClose: () => void
  documentId: ISingleDocument['id']
  documentType: EmailTemplateType
  defaultValues?: Partial<SendEmailFormData>
  onSuccess?: () => void | Promise<void>
}

const AVAILABLE_VARIABLES = [
  { variable: '{{client_name}}', description: 'Naam van de klant' },
  { variable: '{{client_email}}', description: 'E-mailadres van de klant' },
  { variable: '{{client_phone}}', description: 'Telefoonnummer van de klant' },
  { variable: '{{contact_name}}', description: 'Naam van de contactpersoon' },
  { variable: '{{contact_email}}', description: 'E-mailadres van de contactpersoon' },
  { variable: '{{document_type}}', description: 'Type document (factuur/offerte)' },
  { variable: '{{document_number}}', description: 'Documentnummer' },
  { variable: '{{document_reference}}', description: 'Referentie' },
  { variable: '{{document_subject}}', description: 'Betreft' },
  { variable: '{{document_date}}', description: 'Datum van het document' },
  { variable: '{{due_date}}', description: 'Vervaldatum van het document' },
  { variable: '{{total_amount}}', description: 'Totaalbedrag (incl. BTW)' },
  { variable: '{{document_total}}', description: 'Totaalbedrag (incl. BTW)' },
  { variable: '{{total_excl_vat}}', description: 'Totaalbedrag (excl. BTW)' },
  { variable: '{{total_vat}}', description: 'Totaal BTW-bedrag' },
  { variable: '{{vat_breakdown}}', description: 'BTW uitsplitsing per tarief' },
  { variable: '{{vat_21}}', description: 'BTW 21% bedrag' },
  { variable: '{{vat_9}}', description: 'BTW 9% bedrag' },
  { variable: '{{vat_0}}', description: 'BTW 0% bedrag' },
  { variable: '{{subtotal_21}}', description: 'Subtotaal 21% tarief' },
  { variable: '{{subtotal_9}}', description: 'Subtotaal 9% tarief' },
  { variable: '{{subtotal_0}}', description: 'Subtotaal 0% tarief' },
  { variable: '{{company_name}}', description: 'Naam van je bedrijf' },
  { variable: '{{company_email}}', description: 'E-mailadres van je bedrijf' },
  { variable: '{{company_phone}}', description: 'Telefoonnummer van je bedrijf' },
]

export const SendEmailForm = observer((
  { open, onClose, documentId, documentType, defaultValues, onSuccess }: SendEmailFormProps
) => {
  const { email, ui, company } = useStore()
  const [showSuccess, setShowSuccess] = useState(false)
  const [templateLoaded, setTemplateLoaded] = useState(false)
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null)
  const subjectInputRef = useRef<HTMLInputElement>(null)
  const bodyInputRef = useRef<HTMLTextAreaElement>(null)

  // Filter available variables based on company data
  const availableVariables = AVAILABLE_VARIABLES.filter(({ variable }) => {
    const currentCompany = company.current_company
    if (!currentCompany) return true // Show all if no company loaded

    // Filter out company variables that have no value
    if (variable === '{{company_email}}' && !currentCompany.email) return false
    if (variable === '{{company_phone}}' && !currentCompany.phone) return false

    return true
  })

  const form = useForm<SendEmailFormData>({
    defaultValues: {
      recipient_email: defaultValues?.recipient_email || '',
      recipient_name: defaultValues?.recipient_name || '',
      subject: defaultValues?.subject || '',
      body: defaultValues?.body || '',
      attach_pdf: defaultValues?.attach_pdf ?? true,
    },
  })

  // Load default template when dialog opens
  useEffect(() => {
    const loadDefaultTemplate = async () => {
      if (open && !templateLoaded) {
        try {
          // Fetch templates if not already loaded
          if (email.templates_list.length === 0) {
            await email.getTemplates()
          }

          // Find default template for this document type
          const templatesOfType = email.templates_list.filter(
            (template) => template.type === documentType
          )

          // Try to find explicitly marked default template first
          let defaultTemplate = templatesOfType.find((template) => template.is_default)

          // If no default template but only one template exists for this type, use that one
          if (!defaultTemplate && templatesOfType.length === 1) {
            defaultTemplate = templatesOfType[0]
          }

          // Reset form with template data or default values
          form.reset({
            recipient_email: defaultValues?.recipient_email || '',
            recipient_name: defaultValues?.recipient_name || '',
            subject: defaultTemplate?.subject || defaultValues?.subject || '',
            body: defaultTemplate?.body || defaultValues?.body || '',
            attach_pdf: defaultValues?.attach_pdf ?? true,
          })

          setTemplateLoaded(true)
        } catch (error) {
          console.error('Error loading email template:', error)
          // Fallback to default values
          form.reset({
            recipient_email: defaultValues?.recipient_email || '',
            recipient_name: defaultValues?.recipient_name || '',
            subject: defaultValues?.subject || '',
            body: defaultValues?.body || '',
            attach_pdf: defaultValues?.attach_pdf ?? true,
          })
        }
      }

      // Reset template loaded flag when dialog closes
      if (!open && templateLoaded) {
        setTemplateLoaded(false)
      }
    }

    loadDefaultTemplate()
  }, [open, documentType, defaultValues, email, form, templateLoaded])

  const handleClose = () => {
    form.reset()
    setShowSuccess(false)
    setActiveField(null)
    onClose()
  }

  // Detect unavailable variables in text
  const getUnavailableVariables = (text: string): string[] => {
    if (!text) return []

    const variablePattern = /\{\{([^}]+)\}\}/g
    const matches = text.matchAll(variablePattern)
    const unavailable: string[] = []

    for (const match of matches) {
      const fullVariable = match[0] // e.g., "{{company_email}}"
      const isAvailable = availableVariables.some(v => v.variable === fullVariable)
      if (!isAvailable && !unavailable.includes(fullVariable)) {
        unavailable.push(fullVariable)
      }
    }

    return unavailable
  }

  const insertVariable = (variable: string) => {
    const field = activeField || 'body'
    const currentValue = form.getValues(field) || ''
    const inputRef = field === 'subject' ? subjectInputRef : bodyInputRef
    const input = inputRef.current

    if (input) {
      const start = input.selectionStart || currentValue.length
      const end = input.selectionEnd || currentValue.length
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end)

      form.setValue(field, newValue, { shouldValidate: true, shouldDirty: true })

      // Set cursor position after inserted variable
      setTimeout(() => {
        const newPosition = start + variable.length
        input.setSelectionRange(newPosition, newPosition)
        input.focus()
      }, 0)
    } else {
      // Fallback: append to end
      form.setValue(field, currentValue + variable, { shouldValidate: true, shouldDirty: true })
    }
  }

  const onSubmit = async (data: SendEmailFormData) => {
    try {
      // Always attach PDF
      await email.sendDocumentEmail(documentId, { ...data, attach_pdf: true })
      setShowSuccess(true)
      form.reset()

      // Call onSuccess callback to refresh document data
      if (onSuccess) {
        await onSuccess()
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (error: any) {
      // Error handling is already done in the store
      console.error('Error sending email:', error)
    }
  }

  const FormInput = OvFormInput

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        },
      }}>
      <OvFormProvider
        form={form}
        readOnly={false}
        onSubmit={onSubmit as any}
        onCancel={handleClose}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogTitle>Document versturen per e-mail</DialogTitle>
          <DialogContent
            sx={{
              overflowY: 'auto',
              pb: 0,
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
            {showSuccess && (
              <Alert
                severity="success"
                onClose={() => setShowSuccess(false)}>
                E-mail succesvol verzonden!
              </Alert>
            )}

            {!showSuccess && (
              <>
                <Alert severity="info">
                  Vul de gegevens in om dit document per e-mail te versturen. Het PDF bestand kan optioneel als bijlage
                  worden toegevoegd.
                </Alert>

                <FormInput
                  label="E-mailadres ontvanger"
                  name="recipient_email"
                  type="email"
                  required
                  rules={{
                    required: 'E-mailadres is verplicht',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ongeldig e-mailadres',
                    },
                  }}
                />

                <FormInput
                  label="Naam ontvanger (optioneel)"
                  name="recipient_name"
                  type="text"
                />

                <FormInput
                  label="Onderwerp (optioneel)"
                  name="subject"
                  type="text"
                  inputRef={subjectInputRef}
                  onFocus={() => setActiveField('subject')}
                />

                {(() => {
                  const subjectUnavailable = getUnavailableVariables(form.watch('subject') || '')
                  if (subjectUnavailable.length > 0) {
                    return (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          component="div">
                          De volgende variabelen zijn niet beschikbaar (vul de gegevens in bij bedrijfsinstellingen):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {subjectUnavailable.map(variable => (
                            <Chip
                              key={variable}
                              label={variable}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      </Alert>
                    )
                  }
                  return null
                })()}

                <Alert
                  severity="info"
                  sx={{ mt: 1 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom>
                    Beschikbare variabelen
                    {activeField && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, fontWeight: 'normal' }}>
                        (klik om toe te voegen aan {activeField === 'subject' ? 'onderwerp' : 'bericht'})
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {availableVariables.map(({ variable, description }) => (
                      <Chip
                        key={variable}
                        label={variable}
                        onClick={() => insertVariable(variable)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        title={description}
                        sx={{
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Alert>

                <Box sx={{ position: 'relative' }}>
                  <FormInput
                    label="Bericht (optioneel)"
                    name="body"
                    type="textarea"
                    multiline
                    rows={6}
                    inputRef={bodyInputRef}
                    onFocus={() => setActiveField('body')}
                    sx={{
                      '& textarea': {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      display: 'block',
                      color: 'text.secondary',
                    }}>
                    Variabelen worden weergegeven tussen {'{{'} en {'}}'}.
                    Gebruik de chips hierboven om ze in te voegen.
                  </Typography>
                </Box>

                {(() => {
                  const bodyUnavailable = getUnavailableVariables(form.watch('body') || '')
                  if (bodyUnavailable.length > 0) {
                    return (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          component="div">
                          De volgende variabelen zijn niet beschikbaar (vul de gegevens in bij bedrijfsinstellingen):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {bodyUnavailable.map(variable => (
                            <Chip
                              key={variable}
                              label={variable}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      </Alert>
                    )
                  }
                  return null
                })()}
              </>
            )}
          </Box>
        </DialogContent>

        {!showSuccess && (
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
            }}>
            <Tooltip title={TOOLTIPS.CANCEL_ESC}>
              <span>
                <Button
                  onClick={handleClose}
                  disabled={form.formState.isSubmitting || email.loading}>
                  {LABELS.CANCEL}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={TOOLTIPS.SEND_EMAIL}>
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={form.formState.isSubmitting || email.loading}
                  startIcon={
                    (form.formState.isSubmitting || email.loading) && <CircularProgress size={20} />
                  }>
                  {(form.formState.isSubmitting || email.loading) ? 'Verzenden...' : LABELS.SEND}
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        )}
        </form>
      </OvFormProvider>
    </Dialog>
  )
})
